import React, { Component } from 'react';
import { cloneDeep, isNil } from 'lodash';
import {
  Button,
  Checkbox as SemanticCheckbox,
  Dropdown as SemanticDropdown,
  Form as SemanticForm,
  Icon,
  Input as SemanticInput,
  Label as SemanticLabel,
  Radio as SemanticRadio
} from 'semantic-ui-react';
import * as classnames from 'classnames';

const adaptFileEventToValue = delegate => e => {
  return delegate(e.target.files[0]);
};

// Input
export const Tech3Input = ({
  input,
  label,
  placeholder,
  type,
  disabled,
  errorMsg,
  min,
  max,
  meta: { form, touched, error }
}) => {
  error = touched && error;

  const id = `${form}_${input.name}`;

  const fieldClasses = classnames({
    error
  });

  return (
    <SemanticForm.Field className={fieldClasses}>
      <label htmlFor={id}>{label}</label>
      <div className="form-input-container">
        <SemanticInput
          {...input}
          id={id}
          error={error}
          disabled={disabled}
          fluid
          type={type}
          min={min}
          max={max}
          placeholder={placeholder}
        />
        {error && <SemanticLabel floating icon="warning sign" color="red" />}
        {!error && !isNil(errorMsg) && <span className="error">{errorMsg}</span>}
      </div>
    </SemanticForm.Field>
  );
};

// Hidden Input
export const Tech3HiddenInput = ({ input, val, meta: { form } }) => {
  const id = `${form}_${input.name}`;

  return <SemanticInput {...input} id={id} fluid type="hidden" value={val} />;
};

// Search input
export const Tech3InputSearch = ({
  input,
  label,
  placeholder,
  type,
  onClickSearch,
  searchMsg,
  meta: { form, touched, error, warning }
}) => {
  error = touched && typeof error !== 'undefined';

  const id = `${form}_${input.name}`;

  const fieldClasses = classnames({
    error
  });

  return (
    <SemanticForm.Field className={fieldClasses}>
      <label htmlFor={id}>{label}</label>
      <div className="form-input-container search-input-container">
        <SemanticInput
          {...input}
          id={id}
          error={error}
          fluid
          type={type}
          placeholder={placeholder}
          className="input-search-container"
        />
        <Button className="search-button" onClick={onClickSearch} loading={false} type="button">
          <Icon name="search" size="large" />
        </Button>
        {touched &&
          ((error && <span className="error">{error}</span>) ||
            (warning && <span>{warning}</span>))}
        {!error && searchMsg && <span className="error">{searchMsg}</span>}
      </div>
    </SemanticForm.Field>
  );
};

// File input
export const Tech3FileInput = ({
  input: { name, value: noValue, onChange, onBlur, ...inputProps },
  label,
  placeholder,
  type,
  meta: { form, touched, error, warning },
  ...props
}) => {
  error = touched && error;

  const id = `${form}_${name}`;

  const fieldClasses = classnames({
    error
  });

  return (
    <SemanticForm.Field className={fieldClasses}>
      <label htmlFor={id}>{label}</label>
      <div className="form-input-container">
        <SemanticInput
          {...inputProps}
          {...props}
          id={id}
          error={error}
          fluid
          type="file"
          placeholder={placeholder}
          onChange={adaptFileEventToValue(onChange)}
          onBlur={adaptFileEventToValue(onBlur)}
        />
        {error && <SemanticLabel floating icon="warning sign" color="red" />}
      </div>
    </SemanticForm.Field>
  );
};

// Select
export class Tech3Select extends Component {
  constructor(props) {
    super(props);

    this.state = {
      value: props.input.value
    };

    this.setValue = this.setValue.bind(this);
  }

  setValue(value) {
    this.setState({
      value: cloneDeep(value)
    });
  }

  render() {
    let {
      input: { name, onChange, onBlur, onFocus },
      label,
      placeholder,
      options,
      upward,
      disabled,
      meta: { form, touched, error }
    } = this.props;

    const id = `${form}_${name}`;
    error = touched && error;
    return (
      <SemanticForm.Field>
        <label htmlFor={id}>{label}</label>
        <div className="form-input-container">
          <SemanticDropdown
            selection
            scrolling
            fluid
            disabled={disabled}
            upward={upward}
            onChange={(event, data) => {
              onChange(data.value);
              this.setValue(data.value);
            }}
            onBlur={(event, data) => {
              onBlur(data.value);
            }}
            onFocus={(event, data) => {
              onFocus(data.value);
            }}
            value={this.state.value}
            id={id}
            error={error}
            options={options}
            placeholder={placeholder}
          />
          {error && <SemanticLabel floating icon="warning sign" color="red" />}
        </div>
      </SemanticForm.Field>
    );
  }
}

// Checkbox
export const Tech3RCheckbox = ({
  input: { name, onChange, onBlur, onFocus, value },
  label,
  checkValue,
  idCheck,
  isArray,
  selected,
  meta: { form, touched, error }
}) => {
  error = touched && error;

  const fieldClasses = classnames({
    error
  });

  const setValue = data => {
    if (isArray) {
      if (!value) {
        if (data.checked) {
          return [data.value];
        }
      } else {
        const newValue = cloneDeep(value);
        if (data.checked) {
          newValue.push(data.value);
        } else if (value.indexOf(data.value) > -1) {
          newValue.splice(value.indexOf(data.value), 1);
        }
        return newValue;
      }
    } else {
      return data.checked;
    }
  };

  const isChecked = () => {
    if (isArray) {
      return value.indexOf(checkValue) > -1;
    }

    return value;
  };

  const checked = isChecked();

  return (
    <SemanticForm.Field className={fieldClasses}>
      <SemanticCheckbox
        onClick={(e, data) => {
          onChange(setValue(data));
        }}
        value={checkValue}
        id={idCheck}
        name={name}
        label={label}
        checked={checked}
      />
      {error && <SemanticLabel floating icon="warning sign" color="red" />}
    </SemanticForm.Field>
  );
};

// Radio
export const Tech3Radio = ({
  input: { name, onChange, onBlur, onFocus, value },
  label,
  radioValue,
  idRadio,
  disabled,
  meta: { form, touched, error }
}) => {
  const fieldClasses = classnames({
    error: touched && error
  });

  return (
    <SemanticForm.Field className={fieldClasses}>
      <SemanticRadio
        disabled={disabled}
        onChange={(e, data) => {
          onChange(data.value);
        }}
        value={radioValue}
        id={idRadio}
        name={name}
        label={label}
        checked={radioValue === value}
      />
      {error && <SemanticLabel floating icon="warning sign" color="red" />}
    </SemanticForm.Field>
  );
};

// Input time
export const Tech3InputTimeWithIcon = ({
  input,
  label,
  placeholder,
  type,
  disabled,
  meta: { form, touched, error }
}) => {
  error = touched && error;

  const id = `${form}_${input.name}`;

  const fieldClasses = classnames({
    error
  });

  return (
    <SemanticForm.Field className={fieldClasses}>
      <label htmlFor={id}>{label}</label>
      <div className="form-input-container form-input-time-container">
        <SemanticInput
          {...input}
          id={id}
          error={error}
          disabled={disabled}
          fluid
          type={type}
          placeholder={placeholder}
        />
        <i className="clock outline icon" />
        {error && <SemanticLabel floating icon="warning sign" color="red" />}
      </div>
    </SemanticForm.Field>
  );
};



// WEBPACK FOOTER //
// ./src/components/FormElements.js